<template>
  <div class="md-page">
    <base-header v-if="showType" activeIndex="6" />

    <div class="hero original-hero" :style="{ backgroundImage: 'url(' + heroImg + ')' }">
      <div class="hero-inner">
        <div class="md-wrap">
          <div class="crumb">
            <router-link to="/">首页</router-link>
            <span>›</span>
            <router-link to="/Encyclopedia">投资百科</router-link>
            <span>›</span>
            <span>市场深度</span>
          </div>
          <p class="eyebrow">RED LION · TRADING KNOWLEDGE</p>
          <h1>市场深度（深度成交）</h1>
          <p class="lead">理解市场深度，认识交易执行价格与流动性。</p>
          <div class="hero-actions">
            <a class="btn" href="javascript:;" @click.prevent="scrollToTarget('chapter-1')">深入了解市场深度　→</a>
          </div>
        </div>
      </div>
    </div>

    <div class="md-wrap original-layout">
      <aside class="reading-toc">
        <p>阅读目录</p>
        <nav aria-label="文章章节">
          <a
            v-for="item in tocItems"
            :key="item.id"
            href="javascript:;"
            :class="{ active: activeChapter === item.id }"
            @click.prevent="scrollToTarget(item.id)"
          >
            <span>{{ item.no }}</span>{{ item.label }}
          </a>
        </nav>
      </aside>

      <article class="original-article" aria-label="市场深度说明全文">
        <section id="chapter-1" class="original-section">
          <div class="original-heading"><span>01</span><h2>一、市场深度的概念与意义</h2></div>
          <div class="original-text">
            <p>理解市场深度（Depth of Market，简称 DOM）对交易至关重要，它直接决定订单是否能以预期的价格成交。</p>
            <p>市场深度是反映某一时点市场上不同价格档位买入与卖出挂单情况的数据集合，可视为黄金与白银市场的一面“透视镜”。它通常以订单簿（Order Book）的形式呈现，展示不同价格档位上的买入与卖出订单及其数量，从而反映市场的即时流动性状况。市场深度既反映市场流动性，也影响交易执行价格的稳定性。</p>
            <h3>1. 核心要素</h3>
            <div class="document-table">
              <table>
                <thead><tr><th>要素</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td>买入（Bid）</td><td>买家愿意买入黄金或白银的价格与对应数量。</td></tr>
                  <tr><td>卖出（Ask）</td><td>卖家愿意卖出黄金或白银的价格与对应数量。</td></tr>
                  <tr><td>价格档位</td><td>按价格由高到低或由低到高排列的多个价格层级；每一层代表相应价格档位的挂单情况。</td></tr>
                  <tr><td>挂单数量</td><td>各价格档位可成交的合约数量。数量通常与该价位的流动性相关，数量越大通常代表流动性越好。</td></tr>
                </tbody>
              </table>
            </div>
            <h3>2. 订单簿概念示例</h3>
            <div class="document-table">
              <table>
                <thead><tr><th>价格（USD/盎司）</th><th>方向（Buy/Sell）</th><th>数量（手）</th><th>深度（层）</th></tr></thead>
                <tbody>
                  <tr><td>$4,015.40</td><td>Buy</td><td>1,250</td><td>1</td></tr>
                  <tr><td>$4,015.20</td><td>Buy</td><td>2,800</td><td>2</td></tr>
                  <tr><td>$4,015.00</td><td>Buy</td><td>5,200</td><td>3</td></tr>
                  <tr><td>$4,014.80</td><td>Sell</td><td>3,500</td><td>1</td></tr>
                  <tr><td>$4,014.60</td><td>Sell</td><td>4,100</td><td>2</td></tr>
                  <tr><td>$4,014.40</td><td>Sell</td><td>1,800</td><td>3</td></tr>
                </tbody>
              </table>
            </div>
            <div class="original-note">
              <p>提示：上表为市场深度的概念示意，用以说明不同价格档位的挂单情况；实际交易中的市场深度会随行情报价与成交持续变动。</p>
            </div>
          </div>
        </section>

        <section id="chapter-2" class="original-section">
          <div class="original-heading"><span>02</span><h2>二、关于 MT4 / MT5 无法展示完整市场深度的说明</h2></div>
          <div class="original-text">
            <p>许多投资者在交易国际现货黄金、白银时，会注意到在 MT4 或 MT5 平台上通常只能看到当前的买价和卖价，或订单成交后的最终价格，而无法看到不同价格档位上有多少买卖挂单。这并不是平台出错，也不代表市场中只有一个价格，而是与 MT4 / MT5 平台本身的设计有关。</p>
            <p>MT4 与 MT5 是广泛使用的交易终端，其设计初衷是为零售交易者提供高效、稳定的交易执行与报价展示。受限于平台架构及其对场外交易产品（OTC）的支持方式，在国际现货黄金、白银等品种的交易中，终端通常无法向交易者完整展示底层市场的即时深度结构。具体而言，交易者在 MT4 / MT5 界面中看到的，主要是当前最优买价与卖价，或订单成交后的最终执行价格，而非不同价格层级上的全部挂单分布。该显示限制不意味着市场不存在多层级流动性，而是平台未对该类产品提供完整的市场深度 Level 可视化功能。</p>
            <p>在实际交易执行过程中，订单仍基于真实市场流动性进行撮合。当单笔或多笔订单的成交量超过单一价格层级所能承载的流动性时，系统会自动在多个价格层级中完成成交。因此，最终成交结果可能反映为一个或多个价格的加权执行，而交易者在终端中仅能看到最终确认的成交价格。</p>
            <p>交易执行的核心依据始终是当时市场的真实可成交价格，而非终端所显示的有限报价层级。MT4 / MT5 所呈现的价格资讯属于执行结果的展示，而非完整市场结构的映射。因此，在 MT4 / MT5 平台环境下，应以平台实际显示的成交价格作为交易最终结果；该价格已反映当时市场流动性与成交条件。</p>
          </div>
        </section>

        <section id="chapter-3" class="original-section">
          <div class="original-heading"><span>03</span><h2>三、影响市场深度的关键因素</h2></div>
          <div class="original-text">
            <div class="document-table">
              <table>
                <thead><tr><th>关键因素</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td>流动性提供方</td><td>市场深度本质上来源于持续存在的买卖挂单。接入的流动性提供方越多、品质越高，例如国际大型银行、国际金融机构或流动性供应商，市场中可成交的价格层级和数量就越充足，有助于形成更稳定、更有厚度的市场深度，并降低大额交易对价格的冲击。</td></tr>
                  <tr><td>交易时段与市场活跃度</td><td>市场深度会随交易时段而变化。主要金融市场同时活跃时，参与交易的机构与资金增加，挂单更密集，成交更顺畅。对于现货黄金和白银，伦敦与纽约交易时段重叠期间通常是流动性最充足、交易最活跃的窗口，约对应北京时间晚间 20:00 至 24:00；夏令时期间可能提前一小时。</td></tr>
                  <tr><td>市场波动性水准</td><td>在重要经济数据公布或突发事件发生时，市场波动性可能显著上升。部分流动性提供方可能暂时收紧报价或减少挂单数量，导致买卖价差扩大、市场深度变薄，交易中更容易出现滑点或成交价格偏离预期的情况。</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section id="chapter-4" class="original-section">
          <div class="original-heading"><span>04</span><h2>四、市场深度如何影响订单执行</h2></div>
          <div class="original-text">
            <p>市场深度直接决定订单能否顺利成交，以及最终的成交价格。理解市场深度，有助于投资者对成交结果形成合理预期。</p>
            <h3>1. 显示报价与实际执行价格的差异</h3>
            <p>在大多数交易平台上，交易者通常只能看到当前最优买价与最优卖价，即市场深度中的“首层报价”。但在真实市场中，同一时间往往存在多个价格层级的买卖挂单。</p>
            <p>当订单进入市场时，系统会按照成交规则，从最优价格开始，依次在不同价格层级中撮合，直到订单全部成交，或可成交的挂单数量用尽。因此，订单的最终执行价格可能并不完全等同于交易者下单瞬间看到的荧幕报价。这种价格差异并非平台随意调整价格，而是由市场深度结构和实际撮合过程自然形成的结果。</p>
            <div class="original-note">
              <p><strong>示例：</strong>假设平台显示黄金买价为 4,015.00，但该价格层级下仅有 5 手可成交数量。当投资者提交 8 手买入订单时，前 5 手将在 4,015.00 成交，剩余 3 手则可能在下一个价格层级，例如 4,017.20 或更高价格完成成交。在行情快速变化时，也可能出现投资者点击交易时首层挂单已被其他交易者成交，订单直接按照次优价格执行的情况。</p>
            </div>
            <h3>2. 订单被拒绝或仅部分成交</h3>
            <p>当市场深度不足时，并非所有订单都一定能够完全成交。页面列举的常见情形包括市场在短时间内出现剧烈波动、单笔交易量明显大于当时市场可承载的成交数量，以及指定价格档位缺乏足够的对手盘挂单。</p>
            <p>在这些情况下，订单可能被系统拒绝，或只能完成部分成交。市场深度较浅时，大额订单更容易出现成交不完整、滑点明显或执行延迟的情况。</p>
            <h3>3. 大额订单的分拆执行</h3>
            <p>在真实市场环境下，大额订单通常不会在单一价格一次性完成成交，而是可能被系统自动分拆，并在多个价格层级中逐步完成：部分订单在当前最优价格成交，其余部分在次优或更后续的价格成交，整笔订单可能分布在多个价格层级完成。</p>
            <div class="original-note">
              <p><strong>示例：</strong>一个 10 手的黄金买入订单，可能有 4 手在 4,015.00 成交，2 手在 4,019.20 成交，其余 4 手在 4,020.40 成交。最终显示的成交结果，已经综合反映当时市场深度和流动性状况。</p>
            </div>
            <h3>4. 执行价格偏差（滑点）与市场深度的关系</h3>
            <p>执行价格偏差通常是指交易者预期成交价格与实际成交价格之间的差异，其大小与市场深度密切相关。市场深度充足时，订单通常能够在接近预期价格的水准完成成交，价格偏差相对较小；市场深度不足时，订单可能需要在更远的价格层级中完成撮合，执行偏差相对明显。</p>
            <p>在剧烈波动的市场环境下，即使表面上看似存在一定深度，也可能因价格快速跳变，导致实际成交价格与预期存在较大差异。</p>
          </div>
        </section>

        <section id="chapter-5" class="original-section">
          <div class="original-heading"><span>05</span><h2>五、市场深度相关交易说明</h2></div>
          <div class="original-text">
            <p>理解市场深度有助于交易者在不同市场环境中，更合理地安排交易时机、订单规模与下单方式，从而提升整体交易体验。</p>
            <h3>1. 关注流动性较佳的交易时段</h3>
            <p>市场深度并非全天一致，而是随交易时段发生明显变化。对于交易量较大的订单，选择流动性相对充足的时段，有助于提升成交的稳定性和价格的连续性。一般而言，伦敦与纽约交易时段重叠期间，市场参与者最为集中，挂单更密集，市场深度相对更好；该重叠时段通常对应北京时间 20:00–24:00，夏令时期间可能略有调整。</p>
            <div class="document-table">
              <table>
                <thead><tr><th>可能出现较弱市场深度的时段或情形</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td>周五纽约尾盘</td><td>约为北京时间周六凌晨 4:00 前后。临近周末收市，部分流动性退出，市场深度可能明显下降。</td></tr>
                  <tr><td>周一亚洲早盘</td><td>约为北京时间 06:00–08:00。市场在周末后重新开启，挂单尚未完全恢复，价格可能出现跳空。</td></tr>
                  <tr><td>重大经济或政策事件前后</td><td>例如美联储利率决议、非农就业数据发布等。短时间内市场深度可能快速变化，页面提示应谨慎参与即时交易。</td></tr>
                </tbody>
              </table>
            </div>
            <h3>2. 合理规划订单规模</h3>
            <p>在市场深度相对充足的环境下，大额订单更容易被市场吸收；在流动性偏弱或波动较大的行情中，大额订单可能对价格产生较大冲击。因此，在市场深度较浅的时段或行情中，交易者可考虑将大额订单拆分为多笔较小订单分次执行，以降低单次交易对市场的影响，并取得相对更平滑、平均的整体成交价格。</p>
            <h3>3. 根据市场状况选择合适的订单类型</h3>
            <div class="document-table">
              <table>
                <thead><tr><th>订单类型</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td>市价单</td><td>优先保证成交速度，适合需要快速入场或离场的情形。但在市场深度不足或行情快速波动时，成交价格可能偏离预期，滑点风险相对较高。</td></tr>
                  <tr><td>限价单</td><td>可提前设定可接受的成交价格区间，有助于控制成交成本。但在市场深度不足或价格快速变化时，订单可能无法成交，或只能部分成交。</td></tr>
                </tbody>
              </table>
            </div>
            <p>交易者应根据当时的市场深度和交易目的，灵活选择适合的订单类型。</p>
            <h3>4. 理解显示报价与实际执行价格的差异</h3>
            <p>交易平台上所显示的报价通常仅代表当前市场中的最优买价或最优卖价，而非所有可成交价格的完整集合。实际订单执行时，系统会根据市场深度，在多个价格档位中完成撮合。</p>
            <p>因此，在大额订单或市场快速波动的情况下，显示报价与最终成交价格存在一定差异，属于正常市场现象，反映当时的真实流动性和成交条件，而非平台对价格进行人为干预。</p>
            <div class="original-note">
              <p>平台报价仅展示当前最优买卖价格，而订单执行可能涉及市场深度中的多个价格层级，尤其是在大额交易或高速波动的市场环境中。</p>
            </div>
          </div>
        </section>

        <section id="chapter-6" class="original-section">
          <div class="original-heading"><span>06</span><h2>六、市场深度的重要性与风险提示</h2></div>
          <div class="original-text">
            <p>综合来看，理解并关注市场深度，有助于投资者在交易过程中作出更理性、客观的判断。主要意义归纳如下：</p>
            <div class="document-table">
              <table>
                <thead><tr><th>关注维度</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td>理解报价与实际执行价格差异的来源</td><td>平台显示的报价通常仅代表当前最优买卖价格，实际成交价格会受到市场深度及撮合过程的影响。</td></tr>
                  <tr><td>评估和管理大额订单的执行与滑点风险</td><td>市场深度不足或波动加剧时，大额订单更容易对成交价格产生影响；理解这一点有助于合理控制交易规模和执行预期。</td></tr>
                  <tr><td>选择更合适的交易时机与订单策略</td><td>结合市场深度状况，可更有针对性地选择交易时段、订单类型及执行方式，从而提升交易执行的稳定性。</td></tr>
                </tbody>
              </table>
            </div>
            <p>总体而言，市场深度是影响交易执行品质的重要因素之一。对市场深度的理解，有助于投资者在现货黄金、白银等产品的交易中，更全面地评估潜在风险，并在决策时保持合理预期。</p>
            <p>希望通过本文，使投资者能够对市场深度及其在现货黄金、白银交易执行中的实际影响，建立更清晰、客观的认知。</p>
          </div>
        </section>

        <section id="chapter-7" class="original-section">
          <div class="original-heading"><span>07</span><h2>七、免责声明</h2></div>
          <div class="original-text">
            <div class="original-note">
              <p><strong>免责声明：</strong>交易有风险，投资前请充分评估自身的风险承受能力。本文内容仅用于一般性知识分享，不构成任何形式的投资建议、交易指引或收益承诺。</p>
            </div>
          </div>
        </section>
      </article>
    </div>

    <base-footer v-if="showType" />
  </div>
</template>

<script>
import BaseHeader from '@/component/base-header';
import BaseFooter from '@/component/base-footer';
import heroImg from '../../../static/common/marketDepthHero.png';

export default {
  metaInfo: {
    title: '市场深度（深度成交）|红狮金业投资百科',
    meta: [
      { name: 'keywords', content: '市场深度,深度成交,红狮金业,交易执行价格,流动性,订单簿' },
      { name: 'description', content: '理解市场深度，认识交易执行价格与流动性，了解 MT4 / MT5 下订单撮合与滑点机制。' }
    ]
  },
  components: {
    BaseHeader,
    BaseFooter
  },
  data() {
    return {
      heroImg,
      showType: true,
      activeChapter: 'chapter-1',
      tocItems: [
        { no: '01', id: 'chapter-1', label: '市场深度的概念与意义' },
        { no: '02', id: 'chapter-2', label: '关于 MT4 / MT5 无法展示完整市场深度的说明' },
        { no: '03', id: 'chapter-3', label: '影响市场深度的关键因素' },
        { no: '04', id: 'chapter-4', label: '市场深度如何影响订单执行' },
        { no: '05', id: 'chapter-5', label: '市场深度相关交易说明' },
        { no: '06', id: 'chapter-6', label: '市场深度的重要性与风险提示' },
        { no: '07', id: 'chapter-7', label: '免责声明' }
      ]
    }
  },
  created() {
    if (this.$route.query.from) {
      this.showType = false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateToc, { passive: true })
    this.updateToc()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateToc)
  },
  methods: {
    scrollToTarget(id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.activeChapter = id
      }
    },
    updateToc() {
      const ids = this.tocItems.map(item => item.id)
      // 页面已滚动到底：最后一节内容较短，滚不到 155px 判定线，直接点亮最后一条目录
      const doc = document.documentElement
      if (window.innerHeight + Math.floor(window.scrollY) >= doc.scrollHeight - 10) {
        this.activeChapter = ids[ids.length - 1]
        return
      }
      let current = ids[0]
      for (const id of ids) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top < 155) current = id
      }
      this.activeChapter = current
    }
  }
}
</script>

<style lang="less" scoped>
.md-page {
  --red: #fa2314;
  --ink: #0f0f11;
  --muted: #73737d;
  --line: #e9e9ec;
  background: #f5f5f5;
  line-height: 1.7;
  font-family: Arial, "Microsoft YaHei", sans-serif;
  color: var(--ink);

  .md-wrap {
    max-width: 1240px;
    width: calc(100% - 64px);
    margin: auto;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--red);
    color: #fff;
    border: 1px solid var(--red);
    border-radius: 4px;
    padding: 10px 24px;
    font-weight: 600;
    cursor: pointer;
  }

  .hero {
    background-color: #100f11;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    color: #fff;
    padding-top: 80px;
  }

  .hero-inner {
    padding: 54px 0 67px;
  }

  .crumb {
    font-size: 13px;
    color: #bcbcc2;
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 32px;

    a:hover {
      color: #fff;
    }
  }

  .eyebrow {
    font-size: 13px;
    letter-spacing: 2px;
    color: #ffc4b9;
    margin-bottom: 12px;
  }

  .hero h1 {
    font-size: 58px;
    line-height: 1.2;
    margin: 0 0 18px;
  }

  .hero .lead {
    max-width: 570px;
    color: #e0e0e5;
    font-size: 18px;
    margin: 0;
  }

  .hero-actions {
    display: flex;
    gap: 14px;
    margin-top: 30px;
  }

  .original-hero .hero-inner {
    padding: 48px 0 62px;
  }

  .original-hero h1 {
    font-size: 50px;
    line-height: 1.3;
    max-width: 700px;
  }

  .original-layout {
    display: grid;
    grid-template-columns: 230px minmax(0, 1fr);
    gap: 35px;
    align-items: start;
    padding-top: 45px;
    padding-bottom: 60px;
  }

  .reading-toc {
    position: sticky;
    top: 110px;
    padding: 20px 0;

    >p {
      font-size: 13px;
      color: #93939f;
      letter-spacing: 2px;
      padding: 0 14px 15px;
      border-bottom: 1px solid #dedee3;
      margin: 0;
    }

    nav {
      margin-top: 12px;
    }

    a {
      display: flex;
      gap: 12px;
      font-size: 13px;
      line-height: 1.75;
      padding: 12px 14px;
      border-left: 2px solid transparent;
      margin: 3px 0;
      color: #73737d;
      cursor: pointer;

      span {
        font-size: 11px;
        color: #93939f;
        flex-shrink: 0;
        line-height: 2.2;
      }
    }

    a:hover,
    a.active {
      background: #fff;
      color: var(--red);
      border-left-color: var(--red);
    }

    a.active span {
      color: var(--red);
    }
  }

  .original-article {
    min-width: 0;
    background: #fff;
    border: 1px solid #e9e9ec;
    border-radius: 4px;
    padding: 0 40px;
  }

  .original-section {
    padding: 38px 0 40px;
    border-bottom: 1px solid #e9e9ec;
    scroll-margin-top: 105px;

    &:last-child {
      border-bottom: 0;
    }
  }

  .original-heading {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 24px;

    >span {
      font-size: 13px;
      color: var(--red);
      padding-top: 9px;
      border-top: 2px solid var(--red);
      min-width: 25px;
      margin-top: 4px;
    }

    h2 {
      font-size: 25px;
      line-height: 1.55;
      font-weight: 700;
      margin: 0;
      overflow-wrap: anywhere;
    }
  }

  .original-text {
    >p {
      font-size: 15px;
      color: #53535d;
      line-height: 2.1;
      margin: 0 0 18px;
      text-align: justify;

      &:last-child {
        margin-bottom: 0;
      }
    }

    h3 {
      font-size: 19px;
      line-height: 1.6;
      margin: 28px 0 15px;
      color: #18181c;
    }
  }

  .document-table {
    margin: 18px 0 24px;
    border: 1px solid #e4e4e8;
    border-radius: 4px;
    overflow-x: auto;

    table {
      min-width: 500px;
      text-align: left;
      font-size: 14px;
      line-height: 1.95;
      table-layout: auto;
      border-collapse: collapse;
      width: 100%;
    }

    th {
      text-align: left;
      color: #45454d;
      font-size: 13px;
      font-weight: 600;
      background: #f6f6f7;
      padding: 13px 17px;
      border-bottom: 1px solid #dedee3;
      vertical-align: top;
    }

    td {
      text-align: left;
      padding: 14px 17px;
      color: #60606a;
      vertical-align: top;
      border-bottom: 1px solid #ededf0;

      &:first-child {
        font-weight: 600;
        color: #292932;
        min-width: 125px;
      }
    }

    tr:last-child td {
      border-bottom: 0;
    }

    tbody tr:nth-child(even) {
      background: #fcfcfc;
    }
  }

  .original-note {
    margin: 20px 0;
    padding: 17px 20px;
    background: #f6f6f7;
    border: 1px solid #dedee3;

    p {
      font-size: 13px;
      line-height: 1.95;
      color: #45454d;
      margin: 0;
    }
  }

  .original-section:last-child .original-text>p {
    font-size: 13px;
    color: #83838b;
  }
}

@media screen and (min-width: 1264px) and (max-width: 1724px) {
  .md-page {
    .md-wrap {
      max-width: 1000px;
    }

    .hero h1 {
      font-size: 50px;
    }

    .hero .lead {
      font-size: 16px;
    }

    .original-layout {
      grid-template-columns: 210px minmax(0, 1fr);
      gap: 25px;
    }

    .original-article {
      padding: 0 30px;
    }

    .original-hero h1 {
      font-size: 46px;
    }

    .original-heading h2 {
      font-size: 23px;
    }
  }
}

@media screen and (max-width: 1100px) {
  .md-page {
    .hero {
      background-position: 60% center;
    }

    .original-layout {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .reading-toc {
      position: static;
      padding: 0;
      background: #fff;
      border: 1px solid #e9e9ec;
      border-radius: 4px;

      >p {
        padding: 16px 20px 12px;
        margin: 0;
      }

      nav {
        margin: 0;
        display: flex;
        overflow-x: auto;
        padding: 8px;
      }

      a {
        white-space: normal;
        min-width: 150px;
        max-width: 240px;
        font-size: 12px;
        flex-shrink: 0;
        border-left: 0;
        border-bottom: 2px solid transparent;
      }

      a:hover,
      a.active {
        border-bottom-color: var(--red);
        background: #fff5f2;
      }
    }

    .original-article {
      padding: 0 30px;
    }
  }
}

@media screen and (max-width: 700px) {
  .md-page {
    .md-wrap {
      width: calc(100% - 32px);
    }

    .hero {
      padding-top: 80px;
      background-position: 64% center;
    }

    .hero h1,
    .original-hero h1 {
      font-size: 28px;
      max-width: 100%;
      line-height: 1.4;
    }

    .original-hero .hero-inner {
      padding: 22px 0 30px;
      background: rgba(9, 8, 11, 0.55);
    }

    .original-hero .lead {
      font-size: 14px;
    }

    .eyebrow {
      font-size: 10px;
      letter-spacing: 1px;
      margin-bottom: 8px;
    }

    .crumb {
      margin-bottom: 18px;
      font-size: 11px;
      gap: 8px;
      flex-wrap: wrap;
    }

    .hero-actions {
      margin-top: 18px;

      .btn {
        font-size: 13px;
        padding: 9px 16px;
      }
    }

    .original-layout {
      padding-top: 18px;
      padding-bottom: 30px;
      gap: 16px;
    }

    .reading-toc {
      >p {
        padding: 14px 16px 10px;
      }

      nav {
        display: block;
        overflow-x: visible;
        padding: 6px;
      }

      a {
        width: 100%;
        min-width: 0;
        max-width: none;
        font-size: 13px;
        padding: 10px 12px;
        border-left: 2px solid transparent;
        border-bottom: 0;
        white-space: normal;
      }

      a:hover,
      a.active {
        border-bottom-color: transparent;
      }

      a.active {
        border-left-color: var(--red);
      }
    }

    .original-article {
      padding: 0 16px;
    }

    .original-section {
      padding: 24px 0;
      scroll-margin-top: 60px;
    }

    .original-heading {
      gap: 10px;
      margin-bottom: 16px;

      >span {
        font-size: 10px;
      }

      h2 {
        font-size: 18px;
        line-height: 1.5;
        word-break: break-word;
      }
    }

    .original-text {
      word-break: break-word;

      >p {
        font-size: 14px;
        line-height: 1.9;
        margin-bottom: 14px;
      }

      h3 {
        font-size: 16px;
        margin: 20px 0 12px;
        word-break: break-word;
      }
    }

    .hero .lead {
      word-break: break-word;
    }

    .document-table {
      margin: 14px 0 18px;

      table {
        font-size: 13px;
        min-width: 440px;
      }

      th,
      td {
        padding: 10px 12px;
      }

      td:first-child {
        min-width: 90px;
      }
    }
  }
}

@media screen and (max-width: 500px) {
  .md-page {
    .md-wrap {
      width: calc(100% - 24px);
    }

    .hero {
      padding-top: 50px;
    }

    .hero h1,
    .original-hero h1 {
      font-size: 24px;
    }

    .original-hero .hero-inner {
      padding: 18px 0 24px;
    }

    .original-hero .lead {
      font-size: 13px;
    }

    .hero-actions {
      margin-top: 14px;

      .btn {
        font-size: 12px;
        padding: 8px 14px;
      }
    }

    .original-article {
      padding: 0 12px;
    }

    .original-heading {
      h2 {
        font-size: 16px;
      }
    }

    .original-text>p {
      font-size: 13px;
      line-height: 1.85;
    }

    .original-text h3 {
      font-size: 15px;
    }

    .document-table {
      table {
        font-size: 12px;
        min-width: 400px;
      }

      th,
      td {
        padding: 9px 10px;
      }

      td:first-child {
        min-width: 70px;
      }
    }
  }
}
</style>
